// Action selector — 5 stacked icon cells, one per build action (Farm, Road,
// Building, Pillbox, Mine). Click a cell to arm that action; the active cell
// gets a 3px yellow border highlight (.action-cell.armed in index.html).
// Mirrors the original Mac Bolo HUD: WinBolo's tutorial calls out "the
// forest icon (top left of the window)" as the farm tool, and "the second
// button from the top" as road/bridge — confirmed canonical layout.
//
// Icon art: refreshed 46×46 PNGs at
// public/sprites/hud/actions/{farm,road,building,pillbox,mine}.png.
//
// Keyboard 1..5 + Escape are handled by `BuildUI` directly; this sidebar is
// the click-to-arm path. Both write to the same `armedAction` state so the
// visual stays in sync regardless of input source.

import { BuildAction, type BuildActionCode } from '@bolo2k/sim';
import type { BuildUI } from './buildUI.js';

interface ActionSlot {
  action: BuildActionCode;
  iconUrl: string;
  label: string;
}

const SLOTS: ReadonlyArray<ActionSlot> = [
  { action: BuildAction.Farm, iconUrl: '/sprites/hud/actions/farm.png', label: 'Farm [1]' },
  { action: BuildAction.Road, iconUrl: '/sprites/hud/actions/road.png', label: 'Road [2]' },
  { action: BuildAction.Building, iconUrl: '/sprites/hud/actions/building.png', label: 'Wall [3]' },
  { action: BuildAction.Pillbox, iconUrl: '/sprites/hud/actions/pillbox.png', label: 'Pillbox [4]' },
  { action: BuildAction.Mine, iconUrl: '/sprites/hud/actions/mine.png', label: 'Mine [5]' },
];

interface ActionCell {
  panel: HTMLElement;
  action: BuildActionCode;
}

export class ActionSidebar {
  private cells: ActionCell[] = [];
  // Last synced armed action — draw() runs per frame but the armed action
  // only changes on user input, so skip the 5 classList.toggle calls when
  // unchanged. null = never synced (forces the first draw to apply classes).
  private lastArmed: BuildActionCode | null = null;

  constructor(sidebar: HTMLElement, buildUI: BuildUI) {
    sidebar.classList.add('action-sidebar');
    for (const slot of SLOTS) {
      const panel = document.createElement('button');
      panel.type = 'button';
      panel.className = 'bolo-panel action-cell';
      panel.title = slot.label;
      panel.style.backgroundImage = `url('${slot.iconUrl}')`;
      panel.addEventListener('click', () => {
        // Radio-button semantics: clicking arms the action. Clicking the
        // already-armed action is a no-op (BuildUI.armAction early-returns).
        // To switch modes, click a different cell or press 1..5.
        buildUI.armAction(slot.action);
      });
      sidebar.appendChild(panel);

      this.cells.push({ panel, action: slot.action });
    }
  }

  /** Sync visual state from BuildUI's armed action. Called once per frame. */
  draw(buildUI: BuildUI): void {
    const armed = buildUI.armed;
    if (armed === this.lastArmed) return;
    this.lastArmed = armed;
    for (const cell of this.cells) {
      cell.panel.classList.toggle('armed', cell.action === armed);
    }
  }
}
